import axios from 'axios';
import AWS from 'aws-sdk';
import environment from '../../config/environment';

export interface SystemCheckResult {
  success: boolean;
  details: {
    environment: EnvironmentCheckResult;
    api: ApiCheckResult;
    s3: S3CheckResult;
    database: DatabaseCheckResult;
  };
  timestamp: Date;
}

interface EnvironmentCheckResult {
  success: boolean;
  missingVars: string[];
  message: string;
}

interface ApiCheckResult {
  success: boolean;
  endpoints: {
    [key: string]: boolean;
  };
  message: string;
}

interface S3CheckResult {
  success: boolean;
  bucketAccess: boolean;
  uploadTest: boolean;
  message: string;
}

interface DatabaseCheckResult {
  success: boolean;
  tables: {
    [key: string]: boolean;
  };
  message: string;
}

export class SystemCheck {
  private static instance: SystemCheck;
  private readonly API_URL: string;
  private lastCheckResult: SystemCheckResult | null = null;

  private constructor() {
    // Use localhost in development, production API in production
    this.API_URL = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:5000'
      : 'https://api.causeandimpact.org';
    
    console.log('🔧 SystemCheck initialized with API_URL:', this.API_URL);
  }

  public static getInstance(): SystemCheck {
    if (!SystemCheck.instance) {
      SystemCheck.instance = new SystemCheck();
    }
    return SystemCheck.instance;
  }

  /**
   * Run all system checks
   */
  public async runFullCheck(): Promise<SystemCheckResult> {
    try {
      const envCheck = await this.checkEnvironment();
      const apiCheck = await this.checkAPI();
      const s3Check = await this.checkS3();
      const dbCheck = await this.checkDatabase();

      const result: SystemCheckResult = {
        success: envCheck.success && apiCheck && s3Check.success && dbCheck.success,
        details: {
          environment: envCheck,
          api: apiCheck ? {
            success: apiCheck,
            endpoints: {
              health: apiCheck
            },
            message: 'API check completed successfully'
          } : {
            success: false,
            endpoints: {
              health: false
            },
            message: 'API check failed'
          },
          s3: s3Check,
          database: dbCheck
        },
        timestamp: new Date()
      };

      this.lastCheckResult = result;
      return result;
    } catch (error) {
      console.error('System check failed:', error);
      throw error;
    }
  }

  /**
   * Check environment variables
   */
  private async checkEnvironment(): Promise<EnvironmentCheckResult> {
    const env = environment();
    console.log('Current environment config:', env);

    // Check AWS configuration from environment.js
    const missingVars: string[] = [];
    
    if (!env.aws.accessKeyId) {
      missingVars.push('REACT_APP_AWS_ACCESS_KEY_ID');
    }
    if (!env.aws.secretAccessKey) {
      missingVars.push('REACT_APP_AWS_SECRET_ACCESS_KEY');
    }
    if (!env.aws.region) {
      missingVars.push('REACT_APP_AWS_REGION');
    }
    if (!env.aws.s3Bucket) {
      missingVars.push('REACT_APP_AWS_S3_BUCKET');
    }
    if (!env.apiVersion) {
      missingVars.push('REACT_APP_API_VERSION');
    }

    // Debug logging
    console.log('Environment Check:', {
      nodeEnv: env.nodeEnv,
      isProduction: env.isProduction,
      aws: {
        accessKeyId: env.aws.accessKeyId ? 'Set' : 'Not Set',
        secretAccessKey: env.aws.secretAccessKey ? 'Set' : 'Not Set',
        region: env.aws.region,
        s3Bucket: env.aws.s3Bucket,
        receiptBucket: env.aws.receiptBucket
      },
      apiVersion: env.apiVersion,
      apiUrl: env.apiUrl
    });

    return {
      success: missingVars.length === 0,
      missingVars,
      message: missingVars.length === 0 
        ? 'All environment variables are set'
        : `Missing environment variables: ${missingVars.join(', ')}`
    };
  }

  /**
   * Check API health
   */
  private async checkAPI(): Promise<boolean> {
    try {
      console.log('🔍 Checking API health at:', `${this.API_URL}/api/health/api`);
      
      const response = await fetch(`${this.API_URL}/api/health/api`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        // Don't include credentials for local development
        credentials: process.env.NODE_ENV === 'production' ? 'include' : 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ API health check response:', data);
      return data.success;
    } catch (error) {
      console.error('❌ API health check failed:', error);
      throw error;
    }
  }

  /**
   * Check S3 connectivity and permissions
   */
  private async checkS3(): Promise<S3CheckResult> {
    const env = environment();
    
    try {
      // Configure AWS
      AWS.config.update({
        accessKeyId: env.aws.accessKeyId,
        secretAccessKey: env.aws.secretAccessKey,
        region: env.aws.region
      });

      const s3 = new AWS.S3();
      const bucketName = env.aws.s3Bucket;

      console.log('S3 Check - Attempting to check bucket:', {
        bucket: bucketName,
        region: env.aws.region,
        hasCredentials: !!(env.aws.accessKeyId && env.aws.secretAccessKey)
      });

      if (!bucketName) {
        throw new Error('S3 bucket name not configured');
      }

      // Use listObjectsV2 instead of headBucket for better error handling
      const bucketCheck = await s3.listObjectsV2({
        Bucket: bucketName,
        MaxKeys: 1
      }).promise();

      const bucketAccess = true; // If we get here, we have access

      console.log('S3 Check Results:', {
        bucketAccess,
        bucketName,
        region: env.aws.region
      });

      return {
        success: bucketAccess,
        bucketAccess,
        uploadTest: false, // Skip actual upload test for now
        message: bucketAccess ? 'S3 check completed successfully' : 'S3 bucket access failed'
      };
    } catch (error: any) {
      console.error('S3 Check Error:', {
        message: error.message,
        code: error.code,
        statusCode: error.statusCode,
        region: env.aws.region,
        bucket: env.aws.s3Bucket
      });

      // Handle specific AWS errors
      let errorMessage = 'S3 check failed: ';
      if (error.code === 'NoSuchBucket') {
        errorMessage += 'Bucket does not exist';
      } else if (error.code === 'AccessDenied') {
        errorMessage += 'Access denied to bucket';
      } else if (error.code === 'InvalidAccessKeyId') {
        errorMessage += 'Invalid AWS access key';
      } else if (error.code === 'SignatureDoesNotMatch') {
        errorMessage += 'Invalid AWS secret key';
      } else {
        errorMessage += error.message;
      }

      return {
        success: false,
        bucketAccess: false,
        uploadTest: false,
        message: errorMessage
      };
    }
  }

  /**
   * Check database tables and connections
   */
  private async checkDatabase(): Promise<DatabaseCheckResult> {
    try {
      const response = await axios.get('/api/health/database');
      const tables = response.data.tables || {};

      return {
        success: Object.values(tables).every(exists => exists),
        tables,
        message: 'Database check completed'
      };
    } catch (error: any) {
      return {
        success: false,
        tables: {},
        message: `Database check failed: ${error.message}`
      };
    }
  }

  /**
   * Get the last check result
   */
  public getLastCheckResult(): SystemCheckResult | null {
    return this.lastCheckResult;
  }
}

// Example usage:
/*
const systemCheck = SystemCheck.getInstance();

// Run all checks
const checkResult = await systemCheck.runFullCheck();
console.log('System Check Results:', checkResult);

// Generate test data if needed
if (process.env.NODE_ENV === 'development') {
  await systemCheck.generateTestData(5);
}
*/ 